import { FC, ReactNode, useState } from 'react'
import styled from 'styled-components'
import {
  BD, Button, FiltersRow, Folha, FolhaOverlay, FolhaPega, FolhaTopo, SoCelular, SoDesktop,
} from '../styles'

const BarraCelular = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
`

const BotaoFiltros = styled.button`
  flex-shrink: 0;
  height: 44px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
  border: 1px solid ${BD.grayBorder};
  border-radius: 22px;
  background: #fff;
  color: ${BD.black};
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
`

const Contador = styled.span`
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: ${BD.red};
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
`

const Resumo = styled.span`
  flex-grow: 1;
  min-width: 0;
  font-size: 12px;
  color: ${BD.grayText};
`

const CamposFolha = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 16px;
`

const Fechar = styled.button`
  width: 44px;
  height: 44px;
  margin: -2px -2px -2px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: none;
  color: ${BD.grayText};
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
`

const BotaoFolha = styled(Button)`
  width: 100%;
  height: 52px;
  font-size: 16px;
`

export interface FiltrosProps {
  children: ReactNode
  aoAplicar: () => void
  aoLimpar?: () => void
  /** Quantos filtros estão valendo agora, para o contador do botão. */
  ativos?: number
  /** O que a lista mostra com os filtros atuais, ex.: "12 pedidos". */
  resumo?: string
  confirmar?: string
}

/**
 * OP-3619 — a mesma barra de filtros nas duas larguras.
 *
 * No desktop é a linha de campos de sempre. No celular vira um botão com
 * contador e uma folha que sobe: os campos não cabem lado a lado, e a pessoa
 * em campo mexe em um filtro por vez, confirmando no fim.
 */
export const Filtros: FC<FiltrosProps> = ({
  children, aoAplicar, aoLimpar, ativos = 0, resumo, confirmar,
}) => {
  const [aberta, setAberta] = useState(false)

  const aplicar = () => {
    aoAplicar()
    setAberta(false)
  }

  return (
    <>
      <SoDesktop>
        <FiltersRow>
          {children}
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button $variant="secondary" onClick={aoAplicar}>Aplicar</Button>
          </div>
        </FiltersRow>
      </SoDesktop>

      <SoCelular>
        <BarraCelular>
          <BotaoFiltros type="button" onClick={() => setAberta(true)} aria-expanded={aberta}>
            Filtros
            {ativos > 0 && <Contador>{ativos}</Contador>}
          </BotaoFiltros>
          {resumo && <Resumo>{resumo}</Resumo>}
        </BarraCelular>
      </SoCelular>

      {aberta && (
        <>
          <FolhaOverlay $open onClick={() => setAberta(false)} />
          <Folha $open role="dialog" aria-label="Filtros">
            <FolhaPega />
            <FolhaTopo>
              <h2>Filtros</h2>
              {aoLimpar && (
                <Button
                  $variant="secondary"
                  onClick={() => { aoLimpar(); }}
                >
                  Limpar
                </Button>
              )}
              <Fechar type="button" aria-label="Fechar filtros" onClick={() => setAberta(false)}>×</Fechar>
            </FolhaTopo>
            <CamposFolha>{children}</CamposFolha>
            <BotaoFolha onClick={aplicar}>{confirmar || 'Ver resultados'}</BotaoFolha>
          </Folha>
        </>
      )}
    </>
  )
}

export default Filtros
